/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { Fragment, useCallback, useEffect, useMemo } from 'react';

import { IncludeOptionItem, KeepAtMostValueItem, RecordingCount } from 'client-code/onepass';

import { BrandingUiInfoKeys } from '../../../core/entities';
import { SeparatorTypes } from '../../../core/enums';
import { NdvrInteractor, ServicesClientImpl } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { CursorManager, KeyManager } from '../../../core/navigation';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { setOnePassAndRecordingOptionsAction } from '../../../store/settings/userpreferences/onepassandrecordings/onepassandrecordings.actions';
import { onCursorStateChangeOnePassAndRecordingsOptions } from '../../../store/settings/userpreferences/onepassandrecordings/onepassandrecordings.navigation';
import { setOnePassAndRecordingOrMyShowsGetErrorAction } from '../../../store/settings/userpreferences/userpreferences.actions';
import { showWhisper } from '../../../store/whisper/whisper.reducer';
import { ScreenPreview } from '../../common';
import { Seperator } from '../../OverlayDialog/common';
import { WhisperTypes } from '../../Whisper/WhisperTypes';
import { OptionList } from '../ParentalControls/OptionList';
import { SettingsTemplate } from '../SettingsTemplate';

import './OnePassAndRecordingOptions.scss';

interface OnePassOptionDetails {
    optionName: string;
    optionPreview: string;
    optionPreviewNote?: string;
}

const isRecordCounting = (value: KeepAtMostValueItem | undefined): value is RecordingCount => {
    if (!value) {
        return false;
    }
    return value.type === 'RecordingCount';
};

const OnePassAndRecordingOptions = () => {
    const title = Locale.getString(LocaleKeys.SETTINGS_ONEPASS_RECORDING_OPTIONS);
    const description = Locale.getString(LocaleKeys.SETTINGS_ONEPASS_RECORDING_OPTIONS_DESCRIPTION);
    const { selectedIndex, options, ghostMode } = useTivoSelector(
        (state: RootState) => state.onePassAndRecordingOptions,
    );
    const dispatch = useTivoDispatch();

    const { getBrandingUiInfoForKey } = useMemo(() => new ServicesClientImpl(), []);

    const optionsOrder: { [key: string]: OnePassOptionDetails } = {
        include: {
            optionName: `${Locale.getString(LocaleKeys.OPO_OPTION_INCLUDE_TYPE)}:`,
            optionPreview: Locale.getString(LocaleKeys.OPO_OPTION_INCLUDE_TYPE_PREVIEW).replace(
                /DISTRIBUTOR_NAME/g, // uses the global flag (/g) to replace all matches of "DISTRIBUTOR_NAME"
                getBrandingUiInfoForKey(BrandingUiInfoKeys.DISTRIBUTOR_NAME),
            ),
        },
        startFrom: {
            optionName: `${Locale.getString(LocaleKeys.OPO_OPTION_START_FROM)}:`,
            optionPreview: Locale.getString(LocaleKeys.OPO_OPTION_START_FROM_PREVIEW).replace(
                'SEASON_FULL_NAME_LOWER',
                getBrandingUiInfoForKey('seasonFullNameLower'),
            ),
        },
        rentOrBuy: {
            optionName: `${Locale.getString(LocaleKeys.OPO_OPTION_RENT_OR_BUY)}:`,
            optionPreview: Locale.getString(LocaleKeys.OPO_OPTION_RENT_OR_BUY_PREVIEW),
        },
        recordType: {
            optionName: `${Locale.getString(LocaleKeys.OPO_OPTION_RECORD_TYPE)}:`,
            optionPreview: Locale.getString(LocaleKeys.OPO_OPTION_RECORD_TYPE_PREVIEW),
        },
        videoQuality: {
            optionName: `${Locale.getString(LocaleKeys.OPO_OPTION_VIDEO_QUALITY)}:`,
            optionPreview: Locale.getString(LocaleKeys.OPO_OPTION_VIDEO_QUALITY_PREVIEW),
        },
        keepAtMost: {
            optionName: `${Locale.getString(LocaleKeys.OPO_OPTION_KEEP_AT_MOST)}:`,
            optionPreview: Locale.getString(LocaleKeys.OPO_OPTION_KEEP_AT_MOST_PREVIEW),
        },
        keepUntil: {
            optionName: `${Locale.getString(LocaleKeys.OPO_OPTION_KEEP_UNTIL)}:`,
            optionPreview: Locale.getString(LocaleKeys.OPO_OPTION_KEEP_UNTIL_PREVIEW),
            optionPreviewNote: Locale.getString(LocaleKeys.OPO_OPTION_KEEP_UNTIL_PREVIEW_NOTE),
        },
        alwaysShowOverlay: {
            optionName: `${Locale.getString(LocaleKeys.OPO_OPTION_DISPLAY_OPTIONS)}:`,
            optionPreview: Locale.getString(LocaleKeys.OPO_OPTION_DISPLAY_OPTIONS_PREVIEW),
        },
    };

    const getSelectedIndex = useCallback(
        (key: string) => {
            let selectedOptionIndex = 0;
            switch (key) {
                case 'rentOrBuy':
                case 'recordType':
                case 'videoQuality':
                case 'keepUntil':
                case 'alwaysShowOverlay':
                    options?.settingsOptions?.[key]?.optionList?.forEach((el, index) => {
                        if (el[key] === options.settingsValues?.[key]) {
                            selectedOptionIndex = index;
                        }
                    });
                    break;
                case 'include':
                    options?.settingsOptions?.include?.optionList?.forEach(
                        (el: IncludeOptionItem, index) => {
                            if (el.includeValue === options.settingsValues?.include) {
                                selectedOptionIndex = index;
                            }
                        },
                    );
                    break;
                case 'keepAtMost':
                    options?.settingsOptions?.keepAtMost?.optionList?.forEach((el, index) => {
                        if (
                            isRecordCounting(el.keepAtMost) &&
                            isRecordCounting(options.settingsValues?.keepAtMost) &&
                            el.keepAtMost.count === options?.settingsValues?.keepAtMost.count
                        ) {
                            selectedOptionIndex = index;
                        }

                        if (
                            el.keepAtMost?.type === 'AllRecordings' &&
                            options.settingsValues?.keepAtMost?.type === 'AllRecordings'
                        ) {
                            selectedOptionIndex = index;
                        }
                    });
                    break;
                case 'startFrom':
                    options?.settingsOptions?.startFrom?.optionList?.forEach((el, index) => {
                        if (el.startFrom?.type === options.settingsValues?.startFrom?.type) {
                            selectedOptionIndex = index;
                        }
                    });
                    break;
                default:
            }
            return selectedOptionIndex;
        },
        [options],
    );

    const getOptionsLabels = useCallback(
        (setting: string): string[] => {
            const result: string[] = [];
            if (!options?.settingsOptions?.[setting]?.optionList) {
                return result;
            }
            const opt = options.settingsOptions?.[setting].optionList;
            opt.forEach((item) => {
                if (item.label) {
                    result.push(item.label);
                }
            });
            return result;
        },
        [options],
    );

    const [, selectedOptionDetails] =
        Object.entries(optionsOrder).filter(
            ([key]) => options?.settingsOptions?.[key]?.visibility,
        )?.[selectedIndex] || [];

    useEffect(() => {
        const getSettings = async () => {
            const ndvrIteractor = new NdvrInteractor();
            try {
                const response = await ndvrIteractor.getOnepassAndRecordingsUserSettings();
                dispatch(setOnePassAndRecordingOptionsAction(response));
                dispatch(setOnePassAndRecordingOrMyShowsGetErrorAction(false));
            } catch (error) {
                dispatch(setOnePassAndRecordingOptionsAction({}));
                dispatch(setOnePassAndRecordingOrMyShowsGetErrorAction(true));
                dispatch(
                    showWhisper({
                        message: Locale.getString(
                            LocaleKeys.SOMETHING_WENT_WRONG_WHISPER_DESCRIPTION,
                        ),
                        iconType: WhisperTypes.INFO,
                    }),
                );
            }
        };

        getSettings();
    }, [dispatch]);

    const onCursorStateChangeCallback = () => {
        dispatch(onCursorStateChangeOnePassAndRecordingsOptions());
    };

    useEffect(() => {
        const refCursorManager = CursorManager.subscribe(onCursorStateChangeCallback);
        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }
        return () => {
            CursorManager.unsubscribe(refCursorManager);
        };
    }, []);

    return (
        <SettingsTemplate title={title}>
            <div className="settings-common-content one-pass-recording-options">
                <p className="description">{description}</p>
                {Object.entries(optionsOrder)
                    .filter(([key]) => options?.settingsOptions?.[key]?.visibility)
                    .map(([key, option], index) => (
                        <Fragment key={key}>
                            <OptionList
                                id={index}
                                key={`${key}_option`}
                                text={option.optionName}
                                isFocused={selectedIndex === index}
                                horizontalPosition={getSelectedIndex(key)}
                                isGhostModeEnabled={ghostMode}
                                options={getOptionsLabels(key)}
                            />
                            {options?.settingsOptions?.[key]?.insertDividerAfter && (
                                <Seperator key={`${key}_separator`} type={SeparatorTypes.OPTIONS} />
                            )}
                        </Fragment>
                    ))}
                <ScreenPreview classStyle="preview">
                    {selectedOptionDetails && (
                        <>
                            <p> {selectedOptionDetails.optionPreview} </p>
                            {selectedOptionDetails.optionPreviewNote && (
                                <p> {selectedOptionDetails.optionPreviewNote} </p>
                            )}
                        </>
                    )}
                </ScreenPreview>
            </div>
        </SettingsTemplate>
    );
};

export default OnePassAndRecordingOptions;
